import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogClose, Button } from '@herit-fe/heritage-ui';
import { UI_TEXT } from '@/shared/languages';
import { ScrollArea } from './scroll-area';
import { useIsCompact } from './use-is-compact';

export interface FullScreenDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** sr-only 제목(a11y) — 가시 제목은 children 안에서 노출한다. */
  title: string;
  children: ReactNode;
}

/**
 * 적응형 Dialog 셸 — compact(< lg)=풀스크린(dvh + safe-area 로 notch·홈바 흡수, heritage `sm:max-w-md`
 * override), wide(lg+)=일반 centered 다이얼로그(max-w-lg·max-h-85dvh, heritage 기본 rounded/border/shadow
 * /센터링 유지). `useIsCompact`(1024) 로 셸 형태만 분기 — 앱 전역 반응형 경계와 정합.
 *
 * 상단 close 행(고정) + 본문 `ScrollArea`(공용 스크롤바 인셋) 구조라 본문이 버튼 밑으로 깔리지 않고, 긴 본문은
 * 셸 안에서만 스크롤된다(짧으면 wide 모달이 콘텐츠 높이로 shrink). 도메인 무지 — 약관·동의 원문 등이 이 위에 얹힌다.
 * (이름은 관례상 유지 — 모바일 풀스크린이 기본 형태.)
 */
export function FullScreenDialog({ open, onOpenChange, title, children }: FullScreenDialogProps) {
  const compact = useIsCompact();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className={
          compact
            ? 'flex h-dvh w-screen max-w-none flex-col gap-0 overflow-hidden rounded-none p-0 sm:max-w-none'
            : 'flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-200 sm:max-h-[85dvh] sm:h-auto'
        }
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <div className="relative h-0 overflow-visible">
          {/* render 가 Close 의 기본 button 을 Button 으로 치환(중첩 아님) — useRenderElement 가 단일 요소만
              렌더하고 Button 도 native button 으로 끝나므로 nativeButton 경고 없음. */}
          <DialogClose
            render={
              <Button
                variant="ghost"
                size="icon"
                className="absolute z-1 right-5 top-[calc(env(safe-area-inset-top)+1.25rem)]"
              />
            }
          >
            <X className="size-5" />
            <span className="sr-only">{UI_TEXT.COMMON_CLOSE}</span>
          </DialogClose>
        </div>
        {/* 본문 스크롤 — compact(h-dvh)·wide(h-[85dvh]) 둘 다 DialogContent 가 definite height 라 flex-1 이
            남는 높이를 정확히 잡는다. base-ui ScrollArea 의 Viewport 는 size-full(h-full)이라 definite 조상
            높이가 필수 — max-h(auto)면 Viewport 가 콘텐츠만큼 늘어나 스크롤이 안 생긴다(cf. form-sheet). */}
        <ScrollArea className="min-h-0 flex-1 flex flex-col" scrollbarInsetX scrollbarInsetY>
          <div className="p-5 py-[calc(env(safe-area-inset-bottom)+1.25rem)]">{children}</div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
